import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from "@mui/material";

import { 
  Character,
  RollPlus,
  RolledValue,
  TrueCreatureInstance,
  CharacterAdvantage,
  CharacterSheetInterface,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import Roller from "@dndComponents/other/Roller";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import FlexBox from '@components/layout/FlexBox';
import ModalOrDrawer from '@components/display/ModalOrDrawer';
import CharacterInitCustomizations from '../customizations/CharacterInitCustomizations';

interface Props { 
  char: CharacterSheetInterface;
  disabled?: boolean;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterInitiative({ 
  char,
  disabled = false,
}: Props) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [initiative_roll, setInitiativeRoll] = useState<RolledValue[]>([]);
  const [mod, setMod] = useState(0);
  const [adv, setAdv] = useState(0);
  const [advs, setAdvs] = useState<CharacterAdvantage[]>([]);
  const [gr_adv, setGrAdv] = useState(false);
  const [open, setOpen] = useState(false);
  const mounted = useRef(false);

  const char_interface = char instanceof Character ? char.char_interface : char;

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setTimeout(() => {
      let advantage = 0;
      let advantages: CharacterAdvantage[] = [];
      let greater_advantage = false;
      if (char instanceof Character) {
        const { advantage: adv, advantages: advs, greater_advantage: gr_adv2 } = char_util.getAdvantage(char, "Initiative", null);
        advantage = adv;
        advantages = advs;
        greater_advantage = gr_adv2;
      }
      setMod(char_interface.initiative_modifier);
      setAdv(advantage);
      setAdvs(advantages);
      setGrAdv(greater_advantage);
    }, 100);
  }, [char, char_interface]);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [char, recalculated]);

  const renderRoll = () =>{
    if (popoverAnchorEl !== null) {
      return (
        <Roller 
          name="Initiative"
          rolled_values={initiative_roll}
          type="Initiative" 
        />
      );
    }
    return null;
  }

  const renderExtras = () => {
    if (char instanceof Character) {
      return (
        <ModalOrDrawer
          open={open}
          onClose={() => { setOpen(false) }}
        >
          <CharacterInitCustomizations char={char} />
        </ModalOrDrawer>
      );
    }
    return null;
  }

  return (
    <FlexBox direction="column">
      <FlexBox className="centered">
        <DandDButtonBox 
          name={common_util.addPlusMaybe(mod)}
          style={{ 
            fontSize: "24px", 
            width: "50px",
            fontWeight: "bold" 
          }}
          advantage={ adv }
          advantages={ advs }
          greater_advantage={ gr_adv }
          disabled={disabled}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            const rp = new RollPlus();
            rp.flat = mod;
            const roll = char_util.rollD20({ 
              char: char_interface, 
              rolls: [rp], 
              type: "Initiative", 
              use_ability_score: "DEX", 
              advantage: char_interface instanceof TrueCreatureInstance ? undefined : char_interface.body.advantage, 
              greater_advantage: 
              gr_adv });
            setInitiativeRoll(roll);
            setPopoverAnchorEl(event.currentTarget);
          }}
        />
      </FlexBox>
      <FlexBox className="centered clickable" 
        style={{
          fontSize: "11px",
          fontWeight: "bold",
        }}
        onClick={() => {
          if (char instanceof Character) {
            setOpen(true);
          }
        }}>
        Initiative
      </FlexBox>
      { renderExtras() }
      <Popover key="rolls"
        open={ popoverAnchorEl !== null }
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        { renderRoll() }
      </Popover>
    </FlexBox>
  );
}
